import { Component } from 'solid-js';
import colors from '../../styles/colors.module.scss';
import { Polyline } from '../../types';
import { formatApiTimestamp, getNiceDistance, getUserPrefUnit } from '../../utils';
import { ActionButton, ActionButtonItems } from '../ActionButton';
import { Card } from '../Card';
import { GradientText } from '../GradientText';
import { Icon } from '../Icon';
import { Star } from '../Star';
import { MAX_MAP_WIDTH, StaticMap } from '../StaticMap';
import { isMobileLayout, themeClass } from '../ThemeProvider';
import styles from './RideCard.module.scss';

export interface RideCardProps {
  title: string;
  time: number;
  distance: number;
  polyline?: Polyline;
  actions?: ActionButtonItems;
  starred?: boolean;
  onToggleStar?: (starred: boolean) => void;
  notify?: boolean;
  onToggleNotify?: (notify: boolean) => void;
  type?: 'ride' | 'route' | 'stage' | 'virtual';
}

export const RideCard: Component<RideCardProps> = props => {
  const getTime = () => {
    switch (props.type) {
      case 'route':
        return `Created ${formatApiTimestamp(props.time, "EEE MMMM do 'at' h:mma")}`;
      case 'stage':
        return formatApiTimestamp(props.time, 'EEE MMMM do');
      default:
        return formatApiTimestamp(props.time, "EEE MMMM do 'at' h:mma");
    }
  };

  const renderWarning = (message: string) => {
    return (
      <div class={styles.warning}>
        <div class={styles.warningIcon}>
          <Icon type="IconMapPinOff" size="large" />
        </div>
        <p>{message}</p>
      </div>
    );
  };

  const renderMap = () => {
    if (props.type === 'virtual') {
      return renderWarning('Error rendering map. This is a virtual ride.');
    } else if (!props.polyline) {
      return renderWarning('Error rendering map. No map data found for this activity.');
    } else {
      return (
        <StaticMap
          polyline={props.polyline}
          polylineColor={colors.tertiary}
          aspectRatio={isMobileLayout() ? '4:3' : '16:9'}
        />
      );
    }
  };

  const hasMap = props.type !== 'virtual' && props.polyline;

  return (
    <div style={{ 'max-width': `${MAX_MAP_WIDTH}px` }}>
      <Card>
        <div class={themeClass(styles.dark, styles.container)}>
          <div class={styles.header}>
            <div class={styles.type} title={props.type === 'route' ? 'Route' : 'Ride'}>
              <Icon type={props.type === 'route' ? 'IconRoute' : 'IconBike'} size="large" />
            </div>
            <div class={styles.title}>
              <h2>{props.title}</h2>
              <span class={styles.time}>
                <GradientText>{getTime()}</GradientText>
              </span>
            </div>
            <div class={styles.distance}>
              <span class={styles.unit}>{getUserPrefUnit('distance')}</span>
              <span class={styles.value}>{getNiceDistance(props.distance)}</span>
            </div>
            {props.onToggleNotify && hasMap && (
              <button
                type="button"
                class={styles.notify}
                classList={{ [styles.notifyActive]: !!props.notify }}
                title={props.notify ? 'Stop daily notifications for this ride' : 'Get daily 6pm notifications for this ride'}
                aria-pressed={!!props.notify}
                onClick={() => props.onToggleNotify && props.onToggleNotify(!props.notify)}
              >
                <Icon type={props.notify ? 'IconBellFilled' : 'IconBell'} size="large" />
              </button>
            )}
            {props.onToggleStar && (
              <div class={styles.starred}>
                <Star checked={hasMap ? props.starred : false} disabled={!hasMap} onChange={props.onToggleStar} />
              </div>
            )}
          </div>
          <div class={styles.map}>{renderMap()}</div>
          {props.actions && hasMap && <ActionButton items={props.actions} class={styles.actionButton} />}
        </div>
      </Card>
    </div>
  );
};
